import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import { Button, Loader } from 'ncoded-component-library';
import SelectField from 'components/SelectField';
import InputField from 'components/InputField';
import useValidations from 'router/subrouters/Auth/hooks/useValidations';
import validations from 'validations';
import PhoneFieldWrapper from 'components/PhoneFieldWrapper';
import useCallbackRef from 'hooks/useCallbackRef';
import {
  SideDrawerProps,
  SideDrawerRef,
} from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import useBusinessUnits from 'hooks/useBusinessUnits';
import EmailField from 'components/EmailField';
import useMemberSeniorities from 'hooks/useMemberSeniorities';
import useMemberLocations from 'hooks/useMemberLocations';
import CheckboxField from 'components/CheckboxFieldGroup/CheckboxField';
import ResolvePopup from 'components/ResolvePopup';
import InformationcircleIcon from 'icons/Informationcircle.icon';
import { formatPhoneNumber, getPhone } from 'phoneNumberUtils';
import {
  Enterprise,
  SheetEnterpriseMember,
  UpdateEnterpriseMember,
} from 'models/Enterprises';
import {
  findEnterpriseArrayLabel,
  findEnterpriseArrayValue,
} from '../ManagePeopleActions/BulkUploadMembersTable/components/utils';

import './ManageDraftMember.styles.scss';

type ManageDraftMemberProps = {
  className?: string;
  member?: SheetEnterpriseMember;
  enterprise: Partial<Enterprise>;
  onSaveMember: (member: SheetEnterpriseMember) => void;
  onClose: () => void;
} & SideDrawerProps;

const ManageDraftMember: React.ForwardRefRenderFunction<
  SideDrawerRef,
  ManageDraftMemberProps
> = (props, ref) => {
  const { className, member, enterprise, onClose, onSaveMember, ...rest } =
    props;

  const { brandName, memberSeniorities, memberLocations, businessUnits } =
    enterprise || {};

  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  useImperativeHandle(ref, () => drawer, [drawer]);

  const classes = classNames(
    'manage-member-drawer',
    'manage-draft-member',
    className,
  );

  const { t } = useTranslation();

  const commonValidations = useValidations();

  const { businessUnitsOptions } = useBusinessUnits(businessUnits);
  const { memberSenioritiesOptions } = useMemberSeniorities(memberSeniorities);
  const { memberLocationsOptions } = useMemberLocations(memberLocations);

  const initialValues = useMemo(() => {
    const { businessUnitName, seniority, location, phoneNumber, ...rest } =
      member || {};

    return {
      ...rest,
      enterpriseBusinessUnitId: findEnterpriseArrayValue(
        businessUnitName,
        businessUnitsOptions,
      ),
      enterpriseSeniorityId: findEnterpriseArrayValue(
        seniority,
        memberSenioritiesOptions,
      ),
      enterpriseLocationId: findEnterpriseArrayValue(
        location,
        memberLocationsOptions,
      ),
      ...(phoneNumber && {
        phone: getPhone(phoneNumber),
      }),
    };
  }, [
    businessUnitsOptions,
    member,
    memberLocationsOptions,
    memberSenioritiesOptions,
  ]);

  const handleOnClose = useCallback(() => {
    drawer?.setOpen(false);
    onClose();
  }, [drawer, onClose]);

  const handleOnSubmit = useCallback(
    async ({
      phone,
      enterpriseBusinessUnitId,
      enterpriseSeniorityId,
      enterpriseLocationId,
      ...rest
    }: UpdateEnterpriseMember) => {
      const updatedMember = {
        ...member,
        ...rest,
        businessUnitName: findEnterpriseArrayLabel(
          enterpriseBusinessUnitId,
          businessUnitsOptions,
        ),
        seniority: findEnterpriseArrayLabel(
          enterpriseSeniorityId,
          memberSenioritiesOptions,
        ),
        location: findEnterpriseArrayLabel(
          enterpriseLocationId,
          memberLocationsOptions,
        ),
        ...(phone?.countryCode && {
          phoneNumber: phone ? formatPhoneNumber(phone) : null,
        }),
      } as SheetEnterpriseMember;

      onSaveMember(updatedMember);
      handleOnClose();
    },
    [
      businessUnitsOptions,
      member,
      memberLocationsOptions,
      memberSenioritiesOptions,
      onSaveMember,
      handleOnClose,
    ],
  );

  return (
    <SideDrawer
      ref={drawerRef}
      className={classes}
      closeAction={handleOnClose}
      {...rest}
    >
      {!member ? (
        <Loader />
      ) : (
        <Form
          initialValues={initialValues}
          onSubmit={handleOnSubmit}
          render={({ submitting, values, handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <p>
                {t('addEnterprisePeople', {
                  brandName,
                })}
              </p>

              <div>
                <Field
                  name="firstName"
                  label={t('firstName')}
                  component={InputField}
                  required
                  showInitialError
                  validate={commonValidations.name}
                />
                <Field
                  name="lastName"
                  label={t('lastName')}
                  component={InputField}
                  required
                  showInitialError
                  validate={commonValidations.name}
                />
              </div>

              <EmailField required showInitialError />

              <PhoneFieldWrapper showInitialError />

              <Field
                name="title"
                required
                label={t('professionalTitle')}
                showInitialError
                validate={validations.required()}
                component={InputField}
              />

              <Field
                name="enterpriseSeniorityId"
                label={t('seniority')}
                required
                showInitialError
                searchable
                options={memberSenioritiesOptions}
                placeholder=""
                component={SelectField}
                validate={validations.required()}
                key={values.enterpriseSeniorityId}
              />

              <Field
                name="isOfficeManager"
                type="checkbox"
                component={CheckboxField}
                className="office-manager-field"
                label={
                  <div>
                    <span>{t('thisUserIsAnOfficeManager')}</span>
                    <ResolvePopup
                      onHover
                      className="office-manager-field__info-icon"
                      popupContent={t('thisUserIsAnOfficeManagerTooltip')}
                    >
                      <InformationcircleIcon />
                    </ResolvePopup>
                  </div>
                }
              />

              <Field
                name="enterpriseBusinessUnitId"
                label={t('businessUnit')}
                required
                showInitialError
                searchable
                options={businessUnitsOptions}
                placeholder=""
                component={SelectField}
                validate={validations.required()}
                key={values.enterpriseBusinessUnitId}
              />

              <Field
                name="enterpriseLocationId"
                label={t('location')}
                required
                showInitialError
                searchable
                options={memberLocationsOptions}
                placeholder=""
                key={values.enterpriseLocationId}
                component={SelectField}
                validate={validations.required()}
              />

              <Field
                name="linkedinAccount"
                label={t('linkedin')}
                showInitialError
                component={InputField}
                validate={validations.linkedinValidation()}
              />

              <div className="actions">
                <Button type="submit" disabled={submitting}>
                  {t('saveMember')}
                </Button>
              </div>
            </form>
          )}
        />
      )}
    </SideDrawer>
  );
};

export default forwardRef(ManageDraftMember);
